<template>
    <div>
        <ChoiceControl v-if="hasIcons()" :value="selectedValue" @input="selectedValue = $event" :options="selectOptions" :icons="icons"/>
        <select v-else class="form-select custom-select" v-model="selectedValue" :class="{'is-invalid': isInvalid()}">
            <option :value="null"></option>
            <option v-for="option in selectOptions" :value="option">
                {{ option.label }}
            </option>
        </select>
        <div class="invalid-feedback d-block" v-if="isInvalid() && selectedValue === null">
            {{ $t('form.errors.enumNotSelected')}}
        </div>

        <div class="mb-3 mt-4" v-if="isCustomValueOptionSelected()">
            <label class="w-100 form-label">
                {{ $t('form.labels.customEnumValue') }}
                <input class="form-control"
                       :class="{'is-invalid': isInvalid()}"
                       type="text"
                       v-model="customValue"
                       :placeholder="$t(`parameters.${parameterId}.customValuePlaceholder`)">
                <span class="invalid-feedback d-block" v-if="isInvalid() && customValue.trim() === ''">
                    {{ $t('form.errors.customEnumValueIsBlank') }}
                </span>
            </label>
        </div>
    </div>
</template>

<script lang="ts">
import {Control} from '@/composables/Control';
import {enumIconName, iconsForEstateType} from '@/icons';
import {EstimateEstateType} from '@/model/api';
import {isString} from 'lodash-es';
import {computed, defineComponent, inject, PropType, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import ChoiceControl from './ChoiceControl.vue';
import {ChoiceOption, EnumControlValue, EnumOption} from './values';

export default defineComponent({
    components: {ChoiceControl},
    props: {
        value: {
            type: [String, Object] as PropType<EnumControlValue | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
        label: {
            type: String,
            required: true,
        },
        options: {
            type: Array as PropType<EnumOption[]>,
            required: true,
        },
        parameterId: {
            type: String,
            required: true,
        },
        customValueOptions: {
            type: Array as PropType<EnumOption[]>,
            required: true,
        },
    },
    emits: ['input', 'validated'],
    setup(props, {emit}) {
        const {t} = useI18n();
        const estateType = inject<EstimateEstateType>('estateType')!;

        const selectedValue = ref<ChoiceOption | null>(null);
        const customValue = ref('');

        const icons = iconsForEstateType(estateType);

        const selectOptions = computed<ChoiceOption[]>(() => {
            return props.options.map(option => ({
                icon: enumIconName(props.parameterId, option),
                value: option,
                label: t(`parameters.${props.parameterId}.options.${option}`),
            }));
        });

        function getValue(): EnumControlValue | null {
            const value = selectedValue.value?.value;

            if (value === undefined) {
                return null;
            }

            if (props.customValueOptions.includes(value)) {
                return {
                    option: value,
                    customValue: customValue.value.trim(),
                };
            }

            return value;
        }

        function isCustomValueOptionSelected(): boolean {
            return selectedValue.value != null && props.customValueOptions.includes(selectedValue.value.value);
        }

        function validateCurrentValue(): boolean {
            const value = getValue();

            return value === null || (!isCustomValueOptionSelected() || customValue.value.trim() !== '');
        }

        const {updateValue, isInvalid} = Control<EnumControlValue>(props, emit, {
            getValue,
            validateCurrentValue,
        });

        watch(selectedValue, () => {
            updateValue();
        });

        watch(customValue, () => {
            updateValue();
        });

        // created logic
        if (props.value !== null) {
            let selectedOption: string;

            if (isString(props.value)) {
                selectedOption = props.value;
            } else {
                customValue.value = props.value.customValue;
                selectedOption = props.value.option;
            }

            for (const option of selectOptions.value) {
                if (option.value === selectedOption) {
                    selectedValue.value = option;
                    break;
                }
            }
        }

        function hasIcons(): boolean {
            return selectOptions.value.some(option => option.icon in icons);
        }

        return {
            selectedValue,
            customValue,
            selectOptions,
            icons,
            isInvalid,
            hasIcons,
            isCustomValueOptionSelected,
        };
    },
});
</script>
